import React, { useCallback, useEffect, useState } from 'react';
import Horse from '@components/Horse';
import Rider from '@components/Rider';
import Scroller from '@components/Scroller';
import { useTranslation } from 'react-i18next';
import { useStoreState } from 'easy-peasy';
import { jobStoreSelector } from '@model/Job/Job.selectors';
import { selectorIsCompensationVisible } from '@model/showcase-table';
import { useUserPackageVisibility } from '@components/ShareLinkModal/_hooks/use-user-package-visibility';
import { ICON_NAMES } from '@reverse-hr/design-system';
import { getCompensationPackageValues } from '@components/Organisms/GlobalCompensation/_helpers/bonuses-and-benefits';

const Horsejog = ({
  id = 'candidate',
  horse = {},
  person = true,
  modifier,
  showLinks = true,
}) => {
  const { t } = useTranslation();
  const job = useStoreState(jobStoreSelector);
  const jobId = job.id;
  const [showScroller, setShowScroller] = useState(false);
  const isCompensationVisible = useStoreState(selectorIsCompensationVisible);
  const userPackageVisibility = useUserPackageVisibility();
  const candidateAge = horse.yearOfBirth
    ? new Date().getFullYear() - horse.yearOfBirth
    : null;

  const getRiderInfos = horse => {
    let infos = [];

    if (isCompensationVisible && userPackageVisibility.isVisible) {
      const { total } = getCompensationPackageValues({
        candidate: horse,
        isDesired: true,
      });

      if (total > 0) {
        infos.push({
          icon: ICON_NAMES.DESIRED_SALARY,
          label: t('common.formatting.monetary', {
            value: total,
            currency: t('common.currency', { context: job.salaryCurrency }),
          }),
        });
      }
    }

    return infos;
  };

  const handleScroll = useCallback(
    direction => {
      const track = document.querySelector(`#horsejog-track-${id}`);
      if (track) {
        const scroll = 300;
        track.scrollTo({
          top: 0,
          left: Math.max(
            direction === 'prev'
              ? track.scrollLeft - scroll
              : track.scrollLeft + scroll,
          ),
          behavior: 'smooth',
        });
      }
    },
    [id],
  );
  useEffect(() => {
    const track = document.querySelector(`#horsejog-track-${id}`);
    if (track && track.scrollWidth > track.offsetWidth) {
      track.scrollLeft = track.scrollWidth;
      setShowScroller(true);
    }
  }, [id, handleScroll]);

  return (
    <div
      className={`c-horsejog ${
        !!horse.discardedAt ? 'c-horsejog--discarded' : ''
      } ${modifier || ''}`}
    >
      {person ? (
        <div className="c-horsejog__rider">
          <Rider
            candidateId={horse.id}
            jobId={jobId}
            name={t('common.formatting.fullName', {
              firstName: horse.firstName,
              lastName: horse.lastName,
            })}
            infos={getRiderInfos(horse)}
            jobTitle={horse.currentJobTitle}
            showLink={showLinks}
          />
        </div>
      ) : null}
      <div data-testid="horsejog-track" className="c-horsejog__track">
        {showScroller ? (
          <div className="c-horsejog__nav c-horsejog__nav--prev">
            <Scroller
              modifier="c-app-scroller--prev"
              icon="icn-short-left"
              onScroll={() => handleScroll('prev')}
            />
          </div>
        ) : null}
        <div className="c-horsejog__items" id={`horsejog-track-${id}`}>
          {horse.events &&
            horse.events.map((event, index) => {
              return (
                <div key={`event-${index}`} className="c-horsejog__item">
                  <Horse
                    name={horse.firstName}
                    surname={horse.lastName}
                    candidateId={horse.id}
                    jobId={jobId}
                    showLinks={showLinks}
                    showAdditionalInfo={!horse.discardedAt}
                    status={event.type}
                    {...event}
                  />
                </div>
              );
            })}
        </div>
        {showScroller ? (
          <div className="c-horsejog__nav c-horsejog__nav--next">
            <Scroller
              modifier="c-app-scroller--next"
              onScroll={() => handleScroll('next')}
            />
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default Horsejog;
